/** @jsx isolateComponent */
import { isolateComponent, labelStyles } from '@zapier/style-encapsulation';
import { css } from '@emotion/react';
import { Colors } from '../../../../theme/Colors';
import { CTATextContent } from '../CTATextContent';
import { BgColor, DeprecatedBgColor, CTABtnColor } from '../types';
import { getBgColor } from '../utils';
import type { AnchorTarget } from '../../../../ts-types';

export type Props = {
  /**
   * Controls the background of the entire element.
   *
   * Must be one of type `'lava60' | 'ocean40' | 'sun60' | 'white' | 'zapier'
   * | 'night' | 'moss' | 'neutral800' | 'neutral100' | 'earth'`.
   *
   * It also controls the color of the CTA button - background color
   * is white or neutral100, then the CTA button is `primary`. Otherwise, it is `primary-alt`
   * */
  backgroundColor?: BgColor | DeprecatedBgColor;

  /**
   * Heading text content.
   */
  heading: React.ReactNode;
  /**
   * Subheading text content displayed below the heading.
   */
  subHeading: React.ReactNode;
  /**
   * Text content of the CTA button.
   */
  ctaText?: React.ReactNode;
  /**
   * href for the CTA button.
   */
  ctaHref?: string;
  /**
   * Color of the CTA button.
   *
   * Must be one of the type `'primary' | 'primary-alt' | 'primary-dark' | 'brand'`
   */
  ctaBtnColor?: CTABtnColor;
  /**
   * Text displayed above the CTA button.
   */
  description?: React.ReactNode;
  /**
   * CTA button click handler.
   */
  onCtaClick?: (e: React.MouseEvent<HTMLElement, MouseEvent>) => void;
  /**
   * CTA button keyDown handler.
   */
  onCtaKeyDown?: (e: React.KeyboardEvent<HTMLElement>) => void;
  /**
   * max-width in pixels applied to the content area of container.
   */
  maxWidth?: number;
  /**
   * Renders in the space opposite the text.
   * Can be used to render media such as images, videos, or any other React Component.
   */
  children?: React.ReactNode;
  /**
   * The `target` attribute for the CTA button.
   */
  ctaTarget?: AnchorTarget;
};

const Styles = labelStyles('CTAStandard', {
  root: (backgroundColor: DeprecatedBgColor | BgColor) => css`
    background-color: ${Colors[backgroundColor]};
    padding: 40px 20px;
    @media screen and (min-width: 768px) {
      padding: 60px 30px;
    }

    @media screen and (min-width: 1230px) {
      padding: 80px 20px;
    }
  `,
  content: (maxWidth: number) => css`
    display: grid;
    align-items: center;
    grid-template-columns: repeat(1, 1fr);
    gap: 40px;
    max-width: ${maxWidth}px;
    margin: 0 auto;

    @media screen and (min-width: 768px) {
      gap: 90px;
      grid-template-columns: repeat(2, 1fr);
    }
  `,
  mediaContent: css`
    @media screen and (min-width: 768px) {
      margin: 0 auto;
    }
  `,
});

// TODO: Make sure not to render Button when Subscribe form is implemented and used as a children

export const CTAStandard = ({
  backgroundColor = 'white',
  heading,
  subHeading,
  ctaText,
  ctaHref,
  ctaBtnColor,
  description,
  onCtaClick,
  onCtaKeyDown,
  children,
  maxWidth = 1170,
  ctaTarget,
}: Props) => {
  const bgColor = getBgColor(backgroundColor);

  return (
    <div css={Styles.root(bgColor)}>
      <div css={Styles.content(maxWidth)}>
        <CTATextContent
          heading={heading}
          subHeading={subHeading}
          description={description}
          ctaText={ctaText}
          ctaHref={ctaHref}
          ctaBtnColor={ctaBtnColor}
          onCtaClick={onCtaClick}
          onCtaKeyDown={onCtaKeyDown}
          bgColor={bgColor}
          ctaTarget={ctaTarget}
        />
        <div css={Styles.mediaContent} data-testid="media-content">
          {children}
        </div>
      </div>
    </div>
  );
};
